import { Box, CircularProgress } from "@mui/material";
import { logger } from "@platform/browser-logger";
import { RfpData, loadRfp } from "@platform/btx-plan-designer";
import { CoverageTypeEnum } from "@platform/types";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi } from "@platform/api";
import { Typography } from "@platform/design-system";

import { useRfpDetails } from "../../../../hooks/useRfpDetails";
import PageNotFound from "../../../PageNotFound/pageNotFound";

import WorkbenchBuild2 from "./WorkbenchBuild2";

/**
 * Route validator for coverage type URLs
 * Validates planName is valid and exists in RFP, redirects to first coverage if none specified
 */
export const ValidateCoverageRoute = (): React.JSX.Element => {
  const { id: rfpId, planName } = useParams<{
    id: string;
    planName?: string;
  }>();
  const navigate = useNavigate();
  const axiosInstance = useApi();
  const [rfp, setRfp] = useState<RfpData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch RFP details to get the ecoRfpId (UUID)
  const { data: rfpDetails, isLoading: isLoadingDetails } = useRfpDetails({
    rfpId,
  });

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (!rfpDetails?.ecoRfpId) {
        return;
      }

      try {
        setLoading(true);
        const rfpData = await loadRfp(rfpDetails.ecoRfpId, axiosInstance);

        setRfp(rfpData);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (rfpDetails?.ecoRfpId) {
      loadData();
    }
  }, [rfpDetails?.ecoRfpId, axiosInstance]);

  // Redirect to first coverage if no planName provided
  useEffect(() => {
    if (rfp && !planName && !loading) {
      const coverages = rfp.coverages;
      logger.info(
        "[ValidateCoverageRoute] No planName, redirecting to first coverage:",
        {
          coveragesCount: coverages.length,
          firstCoverageType: coverages[0]?.type,
          allCoverageTypes: coverages.map((c) => c.type),
        },
      );
      if (coverages.length > 0) {
        navigate(coverages[0].type, { replace: true });
      }
    }
  }, [rfp, planName, loading, navigate]);

  if (isLoadingDetails || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
        <Typography variant="body-large" sx={{ ml: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error || !rfp) {
    return <PageNotFound />;
  }

  if (planName) {
    logger.info("[ValidateCoverageRoute] Validating planName:", {
      planName,
      validCoverageTypes: Object.values(CoverageTypeEnum),
    });

    // Check if it's a valid CoverageTypeEnum
    const validCoverageTypes = Object.values(CoverageTypeEnum);
    if (!validCoverageTypes.includes(planName as CoverageTypeEnum)) {
      logger.error(
        `[ValidateCoverageRoute] planName not in CoverageTypeEnum: ${planName}`,
      );
      return <PageNotFound />;
    }

    // Check if this coverage exists in the RFP
    const coverages = rfp.coverages;
    logger.info("[ValidateCoverageRoute] Checking if coverage exists:", {
      planName,
      availableCoverages: coverages.map((c) => c.type),
    });
    const coverageExists = coverages.some((cov) => cov.type === planName);
    if (!coverageExists) {
      logger.error(
        `[ValidateCoverageRoute] Coverage does not exist in RFP: ${planName}`,
      );
      return <PageNotFound />;
    }
  }

  return <WorkbenchBuild2 />;
};
